import React from "react";
import { ArrowRight, Mail, Phone, MapPin, Clock } from "lucide-react";
import { FIRM } from "../data/content";
import { useConsultation } from "../components/ConsultationContext";
import Reveal from "../components/Reveal";

export default function Contact() {
  const consult = useConsultation();
  return (
    <div data-testid="contact-page">
      <section className="bg-[#0A192F] text-white pt-44 pb-32 relative overflow-hidden">
        <div className="absolute -top-40 -left-40 h-[36rem] w-[36rem] rounded-full bg-[#C5A059]/10 blur-[120px]" aria-hidden />
        <div className="container-x relative">
          <p className="eyebrow text-[#C5A059]">Contact</p>
          <h1 className="editorial-display text-[clamp(2.8rem,7.4vw,7.5rem)] mt-10 max-w-5xl">
            Connect with <br /><span className="italic text-[#C5A059]">us.</span>
          </h1>
        </div>
      </section>

      <section className="bg-white">
        <div className="container-x py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <Reveal className="lg:col-span-5 space-y-8">
            <div>
              <p className="eyebrow text-[#C5A059]">Office</p>
              <ul className="mt-10 space-y-7 text-[#0A192F]">
                <li className="flex items-start gap-4"><MapPin size={18} className="text-[#C5A059] mt-1 flex-shrink-0" /> <span>{FIRM.contact.address1}<br /><span className="text-[#475569]">{FIRM.contact.address2}</span></span></li>
                <li className="flex items-center gap-4"><Phone size={18} className="text-[#C5A059]" /> <a className="link-underline" href={`tel:${FIRM.contact.phone}`}>{FIRM.contact.phone}</a></li>
                <li className="flex items-center gap-4"><Mail size={18} className="text-[#C5A059]" /> <a className="link-underline" href={`mailto:${FIRM.contact.email}`}>{FIRM.contact.email}</a></li>
                <li className="flex items-center gap-4"><Clock size={18} className="text-[#C5A059]" /> <span>Mon – Sat · 10:00 – 17:00 IST</span></li>
              </ul>
            </div>

            <div className="border border-[#0A192F]/10 aspect-[5/4] overflow-hidden">
              <iframe
                data-testid="contact-map"
                src={FIRM.contact.mapEmbed}
                title="Office location"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={150}>
            <p className="eyebrow text-[#C5A059]">Send a message</p>
            <h2 className="editorial-display text-[#0A192F] text-[clamp(2rem,3.8vw,3.4rem)] mt-8 leading-[1.05] max-w-[22ch]">
              Tell us briefly how we may <span className="italic text-[#C5A059]">assist.</span>
            </h2>
            <p className="text-[#475569] mt-6 leading-relaxed">All inquiries are held in strict professional confidence. We respond within one working day.</p>
            <ContactForm />
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = React.useState({ name: "", message: "" });

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const text = `Hello CA. Nitta Ravi Kishore,\n\nMy name is ${form.name.trim()}.\n\n${form.message.trim()}`;
    const url = `https://wa.me/919440092595?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const inputClass = "mt-3 w-full border-0 border-b border-[#0A192F]/20 px-0 py-3 text-[#0A192F] focus:border-[#C5A059] focus:outline-none bg-transparent placeholder:text-[#94a3b8]";

  return (
    <form onSubmit={submit} className="mt-12 grid grid-cols-1 gap-8" data-testid="contact-form">
      <div>
        <label className="overline text-[10px] text-[#64748B]">Full Name</label>
        <input required minLength={2} value={form.name} onChange={onChange("name")} data-testid="contact-name" className={inputClass} placeholder="Your name" />
      </div>
      <div>
        <label className="overline text-[10px] text-[#64748B]">Message</label>
        <textarea required minLength={5} rows={6} value={form.message} onChange={onChange("message")} data-testid="contact-message" className={`${inputClass} resize-none`} placeholder="Tell us about your matter — kept strictly confidential." />
      </div>
      <div className="flex justify-end pt-2">
        <button type="submit" data-testid="contact-submit" className="btn-primary">
          Send Message <ArrowRight size={16} />
        </button>
      </div>
    </form>
  );
}
